// Push Notification Context for ReBusi
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { requestNotificationPermission, onForegroundMessage } from '@/lib/firebase';
import api from '@/lib/api';
import { toast } from 'sonner';

const PushNotificationContext = createContext(null);

export function PushNotificationProvider({ children, user }) {
  const [fcmToken, setFcmToken] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isSupported, setIsSupported] = useState(true);

  // Check if browser supports notifications
  useEffect(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      setIsSupported(false);
      console.log('Push notifications not supported');
    } else {
      setPermissionStatus(Notification.permission);
    }
  }, []);

  // Request permission and get token when user is logged in
  const enablePushNotifications = useCallback(async () => {
    if (!isSupported || !user) return null;

    try {
      const token = await requestNotificationPermission();
      if (token) {
        setFcmToken(token);
        setPermissionStatus('granted');
        
        // Save token to backend
        try {
          await api.post('/push/register', { fcm_token: token });
          console.log('FCM token registered with backend');
        } catch (error) {
          console.error('Failed to register FCM token:', error);
        }
        
        return token;
      }
    } catch (error) {
      console.error('Failed to enable push notifications:', error);
    }
    return null;
  }, [isSupported, user]);

  // Listen for foreground messages
  useEffect(() => {
    if (!user || permissionStatus !== 'granted') return;

    onForegroundMessage((payload) => {
      console.log('Foreground message received:', payload);
      
      // Show toast notification
      const title = payload.notification?.title || 'ReBusi';
      const body = payload.notification?.body || 'You have a new notification';
      const link = payload.data?.link;

      toast(title, {
        description: body,
        action: link ? {
          label: 'View',
          onClick: () => window.location.href = link
        } : undefined,
        duration: 5000
      });

      // Dispatch custom event for real-time updates
      window.dispatchEvent(new CustomEvent('rebusi-notification', { 
        detail: payload.data 
      }));
    });
  }, [user, permissionStatus]);

  // Auto-enable if already granted
  useEffect(() => {
    if (user && permissionStatus === 'granted' && !fcmToken && isSupported) {
      enablePushNotifications();
    }
  }, [user, permissionStatus, fcmToken, isSupported, enablePushNotifications]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    fcmToken,
    permissionStatus,
    isSupported,
    enablePushNotifications
  }), [fcmToken, permissionStatus, isSupported, enablePushNotifications]);

  return (
    <PushNotificationContext.Provider value={contextValue}>
      {children}
    </PushNotificationContext.Provider>
  );
}

export function usePushNotifications() {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error('usePushNotifications must be used within PushNotificationProvider');
  }
  return context;
}
